import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  PhotoIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { processTextWithVariables } from '~/lib/whatsapp-formatter';

interface Variable {
  id: string;
  name: string;
  position: number;
  example: string;
  component_type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
}

interface TemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  enabled: boolean;
  format?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  text?: string;
  media?: any;
  bodyText?: string;
  footerText?: string;
  buttons?: Array<{
    id: string;
    type: 'QUICK_REPLY' | 'URL';
    text: string;
    url?: string;
  }>;
}

interface TemplateData {
  templateName: string;
  category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
  language: string;
  components: TemplateComponent[];
  variables: Variable[];
}

interface WhatsAppPreviewProps {
  templateData: TemplateData;
}

export const WhatsAppPreview: React.FC<WhatsAppPreviewProps> = ({
  templateData,
}) => {
  const { t } = useTranslation('campaigns');
  
  // Convert HTML to text while preserving line breaks and formatting
  const htmlToText = (html: string) => {
    if (!html) return '';
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Process each node to extract text with proper formatting
    const processNode = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
      }
      
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        let result = '';
        
        // Special handling for <p> tags
        if (element.tagName === 'P') {
          // Add line break before <p> tags (except the first one)
          if (element.previousElementSibling) {
            result += '\n';
          }
          
          // Check if this is an empty paragraph with just <br>
          const hasOnlyBr = element.children.length === 1 && 
                           element.children[0].tagName === 'BR' && 
                           element.textContent?.trim() === '';
          
          if (hasOnlyBr) {
            // Empty paragraph with <br> should create an extra line break
            result += '\n';
          } else {
            // Process child nodes normally
            for (const child of element.childNodes) {
              result += processNode(child);
            }
          }
          
          // Add line break after <p> tags
          result += '\n';
          
          return result;
        }
        
        // Handle <br> tags
        if (element.tagName === 'BR') {
          return '\n';
        }
        
        // Handle bold text with WhatsApp formatting
        if (element.tagName === 'STRONG' || element.tagName === 'B') {
          let innerText = '';
          for (const child of element.childNodes) {
            innerText += processNode(child);
          }
          return `*${innerText}*`;
        }
        
        // Handle italic text with WhatsApp formatting
        if (element.tagName === 'EM' || element.tagName === 'I') {
          let innerText = '';
          for (const child of element.childNodes) {
            innerText += processNode(child);
          }
          return `_${innerText}_`;
        }
        
        // For other elements, process child nodes
        for (const child of element.childNodes) {
          result += processNode(child);
        }
        
        return result;
      }
      
      return '';
    };
    
    let text = processNode(tempDiv);
    
    // Clean up extra line breaks and whitespace, but preserve intentional double line breaks
    text = text
      .replace(/\n{3,}/g, '\n\n') // Replace 3+ line breaks with double line break
      .replace(/^\n+/, '') // Remove leading line breaks
      .replace(/\n+$/, ''); // Remove trailing line breaks
    
    return text;
  };

  // Ensure list markers start on a new line and indent them for preview
  const renderFormattedText = (text: string) => {
    if (!text) return null;
    
    // Convert HTML to plain text first
    const plainText = htmlToText(text);
    
    // Force a newline before list markers (e.g., "1. ", "• ", "- ") if they appear after text
    const normalized = plainText.replace(/([^\n])(?:\s*)(?=(?:\d+\.\s|•\s|-\s))/g, (_m, p1) => `${p1}\n`);
    
    return (
      <div className="text-sm leading-relaxed text-gray-800 whitespace-pre-line">
        {normalized.split('\n').map((line, idx) => {
          const isNumbered = /^\d+\.\s/.test(line);
          const isBullet = /^[•-]\s/.test(line);
          const indentClass = isNumbered || isBullet ? 'pl-4' : '';
          
          // Process WhatsApp formatting in the line
          const processWhatsAppFormatting = (text: string) => {
            // Split by bold markers and process
            const parts = text.split(/(\*[^*]+\*)/g);
            return parts.map((part, partIdx) => {
              if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
                return <strong key={partIdx}>{part.slice(1, -1)}</strong>;
              }
              // Split by italic markers and process
              const italicParts = part.split(/(_[^_]+_)/g);
              return italicParts.map((italicPart, italicIdx) => {
                if (italicPart.startsWith('_') && italicPart.endsWith('_') && italicPart.length > 2) {
                  return <em key={`${partIdx}-${italicIdx}`}>{italicPart.slice(1, -1)}</em>;
                }
                return italicPart;
              });
            });
          };
          
          return (
            <div key={idx} className={indentClass}>
              {processWhatsAppFormatting(line)}
            </div>
          );
        })}
      </div>
    );
  };

  const headerComponent = templateData.components.find(
    (c) => c.type === 'HEADER',
  );
  const bodyComponent = templateData.components.find((c) => c.type === 'BODY');
  const footerComponent = templateData.components.find(
    (c) => c.type === 'FOOTER',
  );
  const buttonsComponent = templateData.components.find(
    (c) => c.type === 'BUTTONS',
  );

  return (
    <div className="sticky top-8">
      {/* Phone Frame */}
      <div className="mx-auto w-80 rounded-3xl border-8 border-gray-800 bg-gray-800 p-2 shadow-2xl">
        <div className="bg-base-100 flex h-[600px] w-full flex-col overflow-hidden rounded-3xl">
          {/* Status Bar */}
          <div className="flex h-8 flex-shrink-0 items-center justify-between bg-gray-900 px-6 text-white">
            <div className="flex items-center space-x-1">
              <div className="h-1 w-1 rounded-full bg-white"></div>
              <div className="h-1 w-1 rounded-full bg-white"></div>
              <div className="h-1 w-1 rounded-full bg-white"></div>
            </div>
            <div className="text-xs font-medium">9:41</div>
            <div className="flex items-center space-x-1">
              <div className="h-1 w-3 rounded-full bg-white"></div>
              <div className="h-1 w-1 rounded-full bg-white"></div>
            </div>
          </div>

          {/* WhatsApp Header */}
          <div className="flex flex-shrink-0 items-center bg-green-600 p-4 text-white">
            <ArrowLeftIcon className="mr-3 h-5 w-5" />
            <div className="flex items-center space-x-3">
              <div className="avatar placeholder">
                <div className=" w-8 rounded-full bg-green-700 text-center text-white">
                  <span className="text-sm">C</span>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium">
                  {t('preview.clientName')}
                </div>
                <div className="text-xs opacity-90">{t('preview.online')}</div>
              </div>
            </div>
          </div>

          {/* Chat Area - Scrollable */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="p-4">
              <div className="space-y-4">
                {/* Template Message */}
                <div className="ml-auto max-w-xs">
                  <div className="overflow-hidden rounded-lg bg-green-100 shadow-sm">
                    {/* Header Component */}
                    {headerComponent?.enabled && (
                      <div className="border-b border-green-200">
                        {headerComponent.format === 'TEXT' &&
                          headerComponent.text && (
                            <div className="p-3 text-sm font-semibold text-gray-800">
                              {processTextWithVariables(headerComponent.text, templateData.variables, 'HEADER')}
                            </div>
                          )}
                        {headerComponent.format === 'IMAGE' &&
                        headerComponent.media ? (
                          <div className="flex aspect-video items-center justify-center bg-gray-200">
                            <img
                              src={
                                headerComponent.media.url ||
                                headerComponent.media.preview ||
                                URL.createObjectURL(headerComponent.media)
                              }
                              alt={headerComponent.media.name}
                              className="h-full w-full object-cover"
                            />
                          </div>
                        ) : (
                          headerComponent.format === 'IMAGE' && (
                            <div className="flex aspect-video items-center justify-center bg-gray-200">
                              <PhotoIcon className="h-8 w-8 text-gray-400" />
                              <span className="ml-2 text-xs text-gray-500">
                                {t('preview.image')}
                              </span>
                            </div>
                          )
                        )}
                        {headerComponent.format === 'VIDEO' &&
                        headerComponent.media ? (
                          <div className="relative flex aspect-video items-center justify-center bg-gray-900">
                            <PlayIcon className="h-12 w-12 text-white opacity-80" />
                            <span className="absolute bottom-2 right-2 rounded bg-black bg-opacity-50 px-1 text-xs text-white">
                              0:30
                            </span>
                            {headerComponent.media.preview && (
                              <img
                                src={headerComponent.media.preview}
                                alt="Video preview"
                                className="absolute inset-0 h-full w-full object-cover opacity-30"
                              />
                            )}
                          </div>
                        ) : (
                          headerComponent.format === 'VIDEO' && (
                            <div className="flex aspect-video items-center justify-center bg-gray-200">
                              <VideoCameraIcon className="h-8 w-8 text-gray-400" />
                              <span className="ml-2 text-xs text-gray-500">
                                {t('preview.video')}
                              </span>
                            </div>
                          )
                        )}
                        {headerComponent.format === 'DOCUMENT' && (
                          <div className="flex items-center bg-gray-100 p-3">
                            <DocumentTextIcon className="mr-2 h-5 w-5 text-gray-400" />
                            <span className="text-xs text-gray-600">
                              {headerComponent.media?.name ||
                                t('preview.document')}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Body Component */}
                    <div className="p-3">
                      {bodyComponent?.bodyText
                        ? renderFormattedText(
                            processTextWithVariables(
                              bodyComponent.bodyText,
                              templateData.variables,
                              'BODY',
                            ),
                          )
                        : (
                            <div className="text-sm leading-relaxed text-gray-800">
                              {t('preview.bodyPlaceholder')}
                            </div>
                          )}
                    </div>

                    {/* Footer Component */}
                    {footerComponent?.enabled && footerComponent.footerText && (
                      <div className="px-3 pb-2">
                        <div className="border-t border-green-200 pt-2 text-xs text-gray-600">
                          {processTextWithVariables(
                            footerComponent.footerText,
                            templateData.variables,
                            'FOOTER',
                          )}
                        </div>
                      </div>
                    )}

                    {/* Buttons Component */}
                    {buttonsComponent?.enabled &&
                      buttonsComponent.buttons &&
                      buttonsComponent.buttons.length > 0 && (
                        <div className="space-y-1 border-t border-green-200 p-2">
                          {buttonsComponent.buttons.map((button, index) => {
                            return (
                              <div key={button.id} className="relative">
                                <button
                                  className={`btn btn-outline btn-sm w-full border-green-300 text-green-700 hover:bg-green-50`}
                                >
                                  {button.type === 'URL' && '🔗 '}
                                  {button.text ||
                                    t('preview.buttonPlaceholder', {
                                      number: index + 1,
                                    })}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                  </div>
                  <div className="mt-1 flex items-center justify-end text-right text-xs text-gray-500">
                    <span>9:41</span>
                    <div className="ml-1 flex space-x-1">
                      <div className="h-1 w-1 rounded-full bg-gray-400"></div>
                      <div className="h-1 w-1 rounded-full bg-blue-500"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
